// GraphQL API service for YardSpotter operations
import { getAuthToken } from '../../utils/cookieUtils';
import { YardCheckContext } from '../../utils/movementVariables';

const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

// Location Information verification lines; see utils/locationVerification.
const LINEAGE_VERIFICATION_FIELDS = 'rtls_seen_at rtls_location last_manual_check_at';
const SPOT_VERIFICATION_FIELDS = 'last_rtls_empty_at last_manual_check_at';

// Helper to make GraphQL requests with automatic retry for network failures
async function graphqlRequest<T = any>(
  query: string,
  variables?: Record<string, any>,
  retries: number = 2
): Promise<T> {
  const authToken = getAuthToken();

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query,
          variables
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const result = await response.json();

      if (result.errors) {
        console.error('GraphQL errors:', result.errors);
        throw new Error(result.errors[0].message);
      }

      return result.data;
    } catch (error: any) {
      // Don't retry GraphQL application errors (they'll fail the same way every time)
      const isNetworkError = error.name === 'AbortError' || (error.message && (
        error.message.includes('Failed to fetch') ||
        error.message.includes('NetworkError') ||
        error.message.includes('network') ||
        error.message.includes('ECONNRESET') ||
        error.message.includes('timeout') ||
        error.message.includes('aborted')
      ));
      if (!isNetworkError) {
        throw error;
      }

      if (attempt < retries) {
        // Wait before retrying: 1s, then 2s
        await new Promise(resolve => setTimeout(resolve, (attempt + 1) * 1000));
        console.warn(`Network error, retrying request (attempt ${attempt + 2}/${retries + 1})...`);
      } else {
        throw error;
      }
    }
  }

  // Unreachable, but TypeScript needs it
  throw new Error('Request failed after retries');
}

// Validate shipment reference number
export async function validateShipmentReference(
  referenceNumber: string,
  facilityId: string,
  trailerUuid?: string
): Promise<{ id: string } | null> {
  const query = `
    query GetShipmentIdByPUID {
      shipment_without_trailer_by_reference_number(
        reference_number: "${referenceNumber.trim()}",
        facility_id: "${facilityId}",
        trailer_uuid: "${trailerUuid || ""}"
      ) {
        id
      }
    }
  `;

  try {
    const data = await graphqlRequest(query);
    return data.shipment_without_trailer_by_reference_number;
  } catch (error: any) {
    // Check if the error is "Shipment not found"
    if (error.message === "Shipment not found") {
      return null;
    }
    throw error;
  }
}

// Create movement task
interface CreateMovementTaskParams {
  trailer_uuid: string;
  movement_type: string;
  /** The facility the trailer is at. */
  facility_id: string;
  /** Set only when the destination is another facility of the campus. */
  destination_facility_id?: string;
  facility_zone_uuid?: string | null;
  yard_spot_uuid?: string | null;
  source_facility_zone_uuid?: string | null;
  destination_facility_zone_uuid?: string | null;
  warehouse_zone_uuid?: string;
  dock_uuid?: string;
  trailer_status_uuid?: string;
  driver_id?: string;
  auto_complete?: boolean;
  shipment_id?: string | null;
  force?: boolean;
  /** The yard check the move was made during, if any. */
  yard_check?: YardCheckContext | null;
}

export async function createMovementTask(params: CreateMovementTaskParams): Promise<{ uuid: string }> {
  const mutation = `
    mutation CreateMovementTask(
      $trailer_uuid: String!,
      $movement_type: String!,
      $facility_id: ID!,
      $destination_facility_id: ID,
      $facility_zone_uuid: String,
      $yard_spot_uuid: String,
      $source_facility_zone_uuid: String,
      $destination_facility_zone_uuid: String,
      $warehouse_zone_uuid: String,
      $dock_uuid: String,
      $trailer_status_uuid: String,
      $driver_id: String,
      $shipment_id: String,
      $force: Boolean,
      $auto_complete: Boolean,
      $yard_check: YardCheckContextInput
    ) {
      create_movement_task(input: {
        trailer_uuid: $trailer_uuid
        movement_type: $movement_type
        facility_id: $facility_id
        destination_facility_id: $destination_facility_id
        facility_zone_uuid: $facility_zone_uuid
        yard_spot_uuid: $yard_spot_uuid
        source_facility_zone_uuid: $source_facility_zone_uuid
        destination_facility_zone_uuid: $destination_facility_zone_uuid
        warehouse_zone_uuid: $warehouse_zone_uuid
        dock_uuid: $dock_uuid
        trailer_status_uuid: $trailer_status_uuid
        driver_id: $driver_id
        shipment_id: $shipment_id
        force: $force
        auto_complete: $auto_complete
        yard_check: $yard_check
      }) {
        uuid
      }
    }
  `;

  const data = await graphqlRequest(mutation, params, 0);
  return data.create_movement_task;
}

// Swap two trailers between adjacent spots with a single GraphQL request
export async function swapTrailersBetweenSpots(
  spot1: CreateMovementTaskParams,
  spot2: CreateMovementTaskParams,
  yardCheck?: YardCheckContext | null
): Promise<{ task1: { uuid: string }, task2: { uuid: string } }> {
  const mutation = `
    mutation SwapTrailers(
      $trailer1_uuid: String!,
      $movement1_type: String!,
      $facility1_id: ID!,
      $facility1_zone_uuid: String,
      $yard1_spot_uuid: String,
      $source1_facility_zone_uuid: String,
      $shipment1_id: String,
      $driver1_id: String,
      $auto1_complete: Boolean,
      $force1: Boolean,
      $trailer2_uuid: String!,
      $movement2_type: String!,
      $facility2_id: ID!,
      $facility2_zone_uuid: String,
      $yard2_spot_uuid: String,
      $source2_facility_zone_uuid: String,
      $shipment2_id: String,
      $driver2_id: String,
      $auto2_complete: Boolean,
      $force2: Boolean,
      $yard_check: YardCheckContextInput
    ) {
      task1: create_movement_task(input: {
        trailer_uuid: $trailer1_uuid
        movement_type: $movement1_type
        facility_id: $facility1_id
        facility_zone_uuid: $facility1_zone_uuid
        yard_spot_uuid: $yard1_spot_uuid
        source_facility_zone_uuid: $source1_facility_zone_uuid
        shipment_id: $shipment1_id
        driver_id: $driver1_id
        auto_complete: $auto1_complete
        force: $force1
        yard_check: $yard_check
      }) {
        uuid
      }
      task2: create_movement_task(input: {
        trailer_uuid: $trailer2_uuid
        movement_type: $movement2_type
        facility_id: $facility2_id
        facility_zone_uuid: $facility2_zone_uuid
        yard_spot_uuid: $yard2_spot_uuid
        source_facility_zone_uuid: $source2_facility_zone_uuid
        shipment_id: $shipment2_id
        driver_id: $driver2_id
        auto_complete: $auto2_complete
        force: $force2
        yard_check: $yard_check
      }) {
        uuid
      }
    }
  `;

  const variables = {
    trailer1_uuid: spot1.trailer_uuid,
    movement1_type: spot1.movement_type,
    facility1_id: spot1.facility_id,
    facility1_zone_uuid: spot1.facility_zone_uuid,
    yard1_spot_uuid: spot1.yard_spot_uuid,
    source1_facility_zone_uuid: spot1.source_facility_zone_uuid,
    shipment1_id: spot1.shipment_id,
    driver1_id: spot1.driver_id,
    auto1_complete: spot1.auto_complete,
    force1: spot1.force,
    trailer2_uuid: spot2.trailer_uuid,
    movement2_type: spot2.movement_type,
    facility2_id: spot2.facility_id,
    facility2_zone_uuid: spot2.facility_zone_uuid,
    yard2_spot_uuid: spot2.yard_spot_uuid,
    source2_facility_zone_uuid: spot2.source_facility_zone_uuid,
    shipment2_id: spot2.shipment_id,
    driver2_id: spot2.driver_id,
    auto2_complete: spot2.auto_complete,
    force2: spot2.force,
    yard_check: yardCheck ?? null
  };

  const data = await graphqlRequest(mutation, variables, 0);
  return data;
}

// Assign trailer to yard spot
interface AssignTrailerParams {
  trailer_number: string;
  scac: string;
  yard_spot_uuid: string;
  status: 'EMPTY' | 'LOADED';
  shipment_id?: string | null;
}

export async function assignTrailerToYardSpot(params: AssignTrailerParams): Promise<{ id: string }> {
  const mutation = `
    mutation AssignTrailer($trailer_number: String!, $scac: String!, $yard_spot_uuid: UUID!, $status: String!, $shipment_id: UUID) {
      assign_trailer_to_yard_spot(
        trailer_number: $trailer_number,
        scac: $scac,
        yard_spot_uuid: $yard_spot_uuid,
        status: $status,
        shipment_id: $shipment_id
      ) {
        id
        trailer_number
        status
      }
    }
  `;

  const data = await graphqlRequest(mutation, params, 0);
  return data.assign_trailer_to_yard_spot;
}

// Unassign trailer from yard spot
export async function unassignTrailerFromYardSpot(yardSpotUuid: string): Promise<boolean> {
  const mutation = `
    mutation UnassignTrailer($yard_spot_uuid: UUID!) {
      unassign_trailer_from_yard_spot(yard_spot_uuid: $yard_spot_uuid) {
        success
      }
    }
  `;

  const data = await graphqlRequest(mutation, { yard_spot_uuid: yardSpotUuid }, 0);
  return data.unassign_trailer_from_yard_spot.success;
}

// Update trailer notes
export async function updateTrailerNotes(activeLineageUuid: string, notes: string): Promise<boolean> {
  const mutation = `
    mutation UpdateTrailerNotes($active_lineage_uuid: UUID!, $notes: String!) {
      update_trailer_notes(active_lineage_uuid: $active_lineage_uuid, notes: $notes) {
        success
        active_lineage {
          uuid
          notes
        }
      }
    }
  `;

  const data = await graphqlRequest(mutation, {
    active_lineage_uuid: activeLineageUuid,
    notes
  }, 0);
  return data.update_trailer_notes.success;
}

// Update trailer shipment relationship
export async function updateTrailerShipment(
  trailerUuid: string,
  shipmentReferenceNumber: string,
  facilityId: string
): Promise<boolean> {
  const mutation = `
    mutation UpdateTrailerShipment($trailer_uuid: UUID!, $shipment_reference_number: String!, $facility_id: UUID!) {
      update_trailer_shipment(
        trailer_uuid: $trailer_uuid,
        shipment_reference_number: $shipment_reference_number,
        facility_id: $facility_id
      ) {
        success
        trailer {
          trailer_uuid
          shipment {
            id
            reference_number
          }
        }
      }
    }
  `;

  const data = await graphqlRequest(mutation, {
    trailer_uuid: trailerUuid,
    shipment_reference_number: shipmentReferenceNumber,
    facility_id: facilityId
  }, 0);
  return data.update_trailer_shipment.success;
}

// Remove shipment relationship from trailer
export async function removeShipmentRelationship(trailerUuid: string): Promise<boolean> {
  const mutation = `
    mutation RemoveShipmentRelationship($trailer_uuid: UUID!) {
      remove_shipment_relationship(trailer_uuid: $trailer_uuid) {
        success
      }
    }
  `;

  const data = await graphqlRequest(mutation, { trailer_uuid: trailerUuid }, 0);
  return data.remove_shipment_relationship.success;
}

// Assign trailer to dock door
interface AssignToDockParams {
  trailer_uuid: string;
  dock_door_uuid: string;
  shipment_id?: string | null;
  auto_complete?: boolean;
}

export async function assignTrailerToDock(params: AssignToDockParams): Promise<{ uuid: string }> {
  const mutation = `
    mutation AssignToDock($trailer_uuid: UUID!, $dock_door_uuid: UUID!, $shipment_id: UUID, $auto_complete: Boolean) {
      assign_trailer_to_dock(
        trailer_uuid: $trailer_uuid,
        dock_door_uuid: $dock_door_uuid,
        shipment_id: $shipment_id,
        auto_complete: $auto_complete
      ) {
        uuid
        status
      }
    }
  `;

  const data = await graphqlRequest(mutation, params, 0);
  return data.assign_trailer_to_dock;
}

// Get shipment by reference number (simple version without trailer_uuid check)
export async function getShipmentByReferenceNumber(
  referenceNumber: string,
  facilityId: string
): Promise<{ id: string } | null> {
  const query = `
    query GetShipmentIdByPUID {
      shipment_by_reference_number(
        reference_number: "${referenceNumber.trim()}",
        facility_id: "${facilityId}"
      ) {
        id
      }
    }
  `;

  try {
    const data = await graphqlRequest(query);
    return data.shipment_by_reference_number;
  } catch (error: any) {
    console.error('Error looking up shipment:', error);
    return null;
  }
}

// Add new trailer
interface AddTrailerParams {
  scac: string;
  trailerNumber: string;
  facilityId: string;
  status: string;
  notes?: string | null;
  facilityZoneUuid?: string | null;
  yardSpotUuid?: string | null;
  force_create_trailer_if_active?: boolean;
}

export async function addTrailer(params: AddTrailerParams): Promise<{ success: boolean; trailer?: { uuid: string; trailerNumber: string }; errors?: any }> {
  const mutation = `
    mutation AddTrailer(
      $scac: String!,
      $trailerNumber: String!,
      $facilityId: ID!,
      $status: String!,
      $notes: String,
      $facilityZoneUuid: String,
      $yardSpotUuid: String,
      $force_create_trailer_if_active: Boolean
    ) {
      addTrailer(input: {
        scac: $scac
        trailerNumber: $trailerNumber
        facilityId: $facilityId
        status: $status
        notes: $notes
        facilityZoneUuid: $facilityZoneUuid
        yardSpotUuid: $yardSpotUuid
        force_create_trailer_if_active: $force_create_trailer_if_active
      }) {
        success
        trailer {
          uuid
          trailerNumber
        }
        errors
      }
    }
  `;

  const data = await graphqlRequest(mutation, params, 0);
  return data.addTrailer;
}

// Update trailer
interface UpdateTrailerParams {
  uuid: string;
  input: {
    notes?: string;
    [key: string]: any;
  };
}

export async function updateTrailer(params: UpdateTrailerParams): Promise<{ uuid: string; trailer_number: string; active_lineage?: any }> {
  const mutation = `
    mutation UpdateTrailer($uuid: String!, $input: TrailerUpdateInput!) {
      updateTrailer(uuid: $uuid, input: $input) {
        uuid
        trailer_number
        active_lineage {
          uuid
          notes
        }
      }
    }
  `;

  const data = await graphqlRequest(mutation, params, 0);
  return data.updateTrailer;
}

// Get task by UUID
export async function getTaskByUuid(taskUuid: string): Promise<any> {
  const query = `
    query GetTask($taskUuid: String!) {
      task(id: $taskUuid) {
        uuid
        task_type {
          type
        }
        task_status {
          status
        }
        from_location
        to_location
        trailer {
          uuid
          trailer_number
        }
        data
      }
    }
  `;

  const data = await graphqlRequest(query, { taskUuid });
  return data.task;
}

// Get yard and facility data
export async function getYardAndFacilityData(
  facilityId: string,
  facilityZoneUuid?: string
): Promise<any> {
  // Build yard query parameters - conditionally include facility_zone_uuid
  const yardParams = facilityZoneUuid 
    ? `facility_id: "${facilityId}", facility_zone_uuid: "${facilityZoneUuid}"`
    : `facility_id: "${facilityId}"`;

  // When filtering by zone, exclude facility-wide summaries
  const summaryFields = facilityZoneUuid ? '' : `
        zoneSummary {
          facilityZoneName
          facilityZoneUuid
          totalYardSpots
        }
        statusSummary {
          available_yard_spots
          empty_trailers
          loaded_inbound
          loaded_outbound
        }`;

  const query = `
    query GetYardAndFacilityData {
      yard(${yardParams}) {
        facility_id
        facility_zones {
          facility_zone_name
          facility_zone_uuid
          zone_type
          configuration
          trailers {
            uuid
            trailerNumber
            carrier{
              name
            }
            active_lineage {
              uuid
              notes
              created_at
              check_in_time
              ${LINEAGE_VERIFICATION_FIELDS}
              trailer_state{
                shipment {
                  id
                  referenceNumber
                  direction
                }
                driver {
                  id
                  firstName
                  lastName
                }
              }
            }
            status {
              name
            }
            data
          }
          yard_spots {
            yard_spot_uuid
            yard_spot_name
            yard_spot_facility_zone_uuid
            yard_spot_status
            trailer_lineage_uuid
            trailer_state_uuid
            trailer_name
            trailer_uuid
            carrier_name
            shipment_id
            shipment_reference_number
            driver_id
            driver_name
            order_number
            configuration
            ${SPOT_VERIFICATION_FIELDS}
            shipment {
              id
              referenceNumber
              direction
            }
            driver {
              id
              firstName
              lastName
            }
            trailer {
              uuid
              trailerNumber
              carrier{
                name
              }
              active_lineage {
                uuid
                notes
                created_at
              check_in_time
              ${LINEAGE_VERIFICATION_FIELDS}
              }
              status {
                name
              }
              data
            }
          }
        }${summaryFields}
      }
      facility(id: "${facilityId}") {
        id
        configuration
      }
      facilityLocks(facilityId: "${facilityId}") {
        reason
        resource_uuid
        model_name
        operation_type
        created_by
        task_uuid
        created_at
        expires_at
        namespace
        facility_id
        resource {
            ... on YardSpotObject {
              __typename
              uuid
              name
            }
            ... on DockObject {
              __typename
              uuid
              name
            }
            ... on Trailer {
              __typename
              uuid
              trailerNumber
            }
          }
        }
      }
  `;

  const data = await graphqlRequest(query);
  return data;
}

// Get full unfiltered yard and warehouse data for modals (background loading)
export async function getYardAndWarehouseForModals(facilityId: string): Promise<any> {
  const query = `
    query GetYardAndWarehouseForModals {
      yard(facility_id: "${facilityId}") {
        facility_id
        facility_zones {
          facility_zone_name
          facility_zone_uuid
          zone_type
          configuration
          trailers {
            uuid
            trailerNumber
            carrier {
              name
            }
            active_lineage {
              uuid
              notes
              created_at
              check_in_time
              ${LINEAGE_VERIFICATION_FIELDS}
              trailer_state {
                shipment {
                  id
                  referenceNumber
                  direction
                }
                driver {
                  id
                  firstName
                  lastName
                }
              }
            }
            status {
              name
            }
            data
          }
          yard_spots {
            yard_spot_uuid
            yard_spot_name
            yard_spot_facility_zone_uuid
            yard_spot_status
            trailer_lineage_uuid
            trailer_state_uuid
            trailer_name
            trailer_uuid
            carrier_name
            shipment_id
            shipment_reference_number
            driver_id
            driver_name
            order_number
            configuration
            ${SPOT_VERIFICATION_FIELDS}
            shipment {
              id
              referenceNumber
              direction
            }
            driver {
              id
              firstName
              lastName
            }
            trailer {
              uuid
              trailerNumber
              carrier {
                name
              }
              active_lineage {
                uuid
                notes
                created_at
              check_in_time
              ${LINEAGE_VERIFICATION_FIELDS}
              }
              status {
                name
              }
              data
            }
          }
        }
      }
      warehouse(facilityId: "${facilityId}") {
        facilityId
        warehouseZones {
          zoneUuid
          zoneName
          configuration
          docks {
            dockUuid
            dockName
            dockStatus
            order_number
            trailer_name
            trailer_uuid
            carrier_name
            configuration
            trailer {
              active_lineage {
                uuid
                notes
                created_at
              check_in_time
              }
            }
            shipment {
              id
              referenceNumber
              direction
            }
            driver {
              id
              firstName
              lastName
            }
          }
        }
      }
    }
  `;

  const data = await graphqlRequest(query);
  return data;
}

// Get warehouse data only (for dock doors)
export async function getWarehouseData(facilityId: string): Promise<any> {
  const query = `
    query GetWarehouseData {
      warehouse(facilityId: "${facilityId}") {
        facilityId
        warehouseZones {
          zoneUuid
          zoneName
          configuration
          docks {
            dockUuid
            dockName
            dockStatus
            order_number
            trailer_name
            trailer_uuid
            carrier_name
            configuration
            trailer {
              active_lineage {
                uuid
                notes
                created_at
              check_in_time
              }
            }
            shipment {
              id
              referenceNumber
              direction
            }
            driver {
              id
              firstName
              lastName
            }
          }
        }
      }
    }
  `;

  const data = await graphqlRequest(query);
  return data;
}

// Get all trailers in facility (includes unknown locations)
export async function getTrailersInFacility(facilityId: string): Promise<any> {
  const query = `
    query GetTrailersInFacility($facilityId: ID!) {
      trailers_in_facility(facility_id: $facilityId) {
        uuid
        trailer_number
        carrier {
          name
        }
        active_lineage {
          uuid
          notes
          created_at
          check_in_time
          trailer_state {
            shipment {
              id
              reference_number
            }
          }
        }
        status {
          name
        }
        current_location {
          ... on YardSpotObject {
            __typename
            uuid
            name
            facility_zone {
              name
              uuid
            }
          }
          ... on FacilityZoneObject {
            __typename
            uuid
            name
          }
          ... on DockObject {
            __typename
            uuid
            name
          }
        }
      }
    }
  `;

  const data = await graphqlRequest(query, { facilityId });
  return data.trailers_in_facility;
}

// Create audit entry
interface CreateAuditParams {
  action: string;
  model: string;
  model_id: string;
  facility_id: string;
  params: any; // Object that will be sent as JSON
}

export async function createAudit(params: CreateAuditParams): Promise<any> {
  const mutation = `
    mutation CreateAudit(
      $action: String!,
      $model: String!,
      $model_id: ID!,
      $facility_id: ID!,
      $params: Json
    ) {
      create_audit(
        action: $action
        model: $model
        model_id: $model_id
        facility_id: $facility_id
        params: $params
      ) {
        uuid
      }
    }
  `;

  // Stringify params for GraphQL Json type
  const { params: paramsObj, ...rest } = params;
  const variables = {
    ...rest,
    params: JSON.stringify(paramsObj)
  };
  const data = await graphqlRequest(mutation, variables, 0);
  return data.create_audit;
}

// Spotter trucks for the Yard Check page
export interface SpotterTruck {
  uuid: string;
  name: string;
  account_id: string;
  ziggy_uuid: string | null;
}

export interface YardCheckSpotterContext {
  account_id: string;
  requires_spotter_truck: boolean;
  spotter_trucks: SpotterTruck[];
}

export async function getYardCheckSpotterContext(facilityId: string): Promise<YardCheckSpotterContext> {
  const query = `
    query YardCheckSpotterContext($facility_id: ID!) {
      yard_check_spotter_context(facility_id: $facility_id) {
        account_id
        requires_spotter_truck
        spotter_trucks { uuid name account_id ziggy_uuid }
      }
    }
  `;

  const data = await graphqlRequest(query, { facility_id: facilityId });
  return data.yard_check_spotter_context;
}

// The facility's account's truck by this number, created if there is none
export async function createSpotterTruck(facilityId: string, name: string): Promise<SpotterTruck> {
  const mutation = `
    mutation CreateSpotterTruck($facility_id: ID!, $name: String!) {
      create_spotter_truck(facility_id: $facility_id, name: $name) {
        uuid name account_id ziggy_uuid
      }
    }
  `;

  const data = await graphqlRequest(mutation, { facility_id: facilityId, name }, 0);
  return data.create_spotter_truck;
}

// Export API URL for external use if needed
export { GRAPHQL_API_URL };

